import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  positions: [],
  groups: [], // List of groups from /positions/init
  selectedPosition: null,
  loading: false,
  error: null,
  successMessage: null,
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'name',
  sortDirection: 'asc',
  // Filtering
  filterName: '',
};

const positionsSlice = createSlice({
  name: 'positions',
  initialState,
  reducers: {
    // Fetch init data (groups)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.groups = action.payload.officerGroups || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch positions
    fetchPositions: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPositionsSuccess: (state, action) => {
      state.loading = false;
      state.positions = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchPositionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch position by ID
    fetchPositionById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPositionByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedPosition = action.payload;
      state.error = null;
    },
    fetchPositionByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add position
    addPosition: (state) => {
      state.loading = true;
      state.error = null;
    },
    addPositionSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    addPositionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update position
    updatePosition: (state) => {
      state.loading = true;
      state.error = null;
    },
    updatePositionSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    updatePositionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete position
    deletePosition: (state) => {
      state.loading = true;
      state.error = null;
    },
    deletePositionSuccess: (state, action) => {
      state.loading = false;
      state.positions = state.positions.filter((pos) => pos.id !== action.payload);
      state.error = null;
      state.successMessage = action.payload.message;
    },
    deletePositionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeletePositions: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeletePositionsSuccess: (state, action) => {
      state.loading = false;
      state.positions = state.positions.filter((pos) => !action.payload.includes(pos.id));
      state.error = null;
      state.successMessage = action.payload.message;
    },
    bulkDeletePositionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filtering
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchPositions,
  fetchPositionsSuccess,
  fetchPositionsFailure,
  fetchPositionById,
  fetchPositionByIdSuccess,
  fetchPositionByIdFailure,
  addPosition,
  addPositionSuccess,
  addPositionFailure,
  updatePosition,
  updatePositionSuccess,
  updatePositionFailure,
  deletePosition,
  deletePositionSuccess,
  deletePositionFailure,
  bulkDeletePositions,
  bulkDeletePositionsSuccess,
  bulkDeletePositionsFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  clearError,
  clearSuccessMessage,
} = positionsSlice.actions;

// Selectors
export const selectPositions = (state) => state.positions?.positions || [];
export const selectGroups = (state) => state.positions?.groups || [];
export const selectSelectedPosition = (state) => state.positions?.selectedPosition;
export const selectPositionsLoading = (state) => state.positions?.loading || false;
export const selectPositionsError = (state) => state.positions?.error;
export const selectPositionsSuccessMessage = (state) => state.positions?.successMessage;
export const selectPositionsTotal = (state) => state.positions?.total || 0;
export const selectPositionsPage = (state) => state.positions?.page || 1;
export const selectPositionsPageSize = (state) => state.positions?.pageSize || 10;
export const selectPositionsSortColumn = (state) => state.positions?.sortColumn || 'name';
export const selectPositionsSortDirection = (state) => state.positions?.sortDirection || 'asc';
export const selectPositionsFilterName = (state) => state.positions?.filterName || '';

export default positionsSlice.reducer;
